import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Code, 
  Smartphone, 
  Server, 
  Database, 
  Brain, 
  Globe,
  Briefcase
} from 'lucide-react';
import { Skill, Experience } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * About section component showcasing Nadir's profile
 * Features skills, experience, and personal information
 */

const AboutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[16]};
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[12]};
  }
`;

const ProfileCard = styled(motion.div)`
  background: ${props => props.theme.gradients.cosmic};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: ${props => props.theme.spacing[8]};
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${props => props.theme.patterns.floating}
    opacity: 0.4;
    pointer-events: none;
  }
`;

const ProfileImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing[6]};
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const ProfileName = styled.h2`
  font-size: ${props => props.theme.typography.sizes['3xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing[2]};
  color: white;
`;

const ProfileTitle = styled.p`
  font-size: ${props => props.theme.typography.sizes.lg};
  text-align: center;
  opacity: 1;
  margin-bottom: ${props => props.theme.spacing[6]};
  color: white;
`;

const ProfileDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  opacity: 1;
  color: white;
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[4]};
`;

const StatItem = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing[4]};
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${props => props.theme.borderRadius.lg};
  backdrop-filter: blur(10px);
`;

const StatNumber = styled.div`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  margin-bottom: ${props => props.theme.spacing[1]};
  color: white;
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.sizes.sm};
  opacity: 1;
  color: white;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[8]};
`;

const SkillsSection = styled.div``;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.semibold};
  margin-bottom: ${props => props.theme.spacing[6]};
  color: ${props => props.theme.colors.text};
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing[4]};
`;

const SkillCard = styled(motion.div)<{ $category: string }>`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing[6]};
  border-left: 4px solid ${props => {
    const colors = {
      frontend: props.theme.colors.secondary.purple,
      backend: props.theme.colors.secondary.green,
      mobile: props.theme.colors.secondary.orange,
      database: props.theme.colors.secondary.teal,
      'ai-ml': props.theme.colors.secondary.pink,
      tools: props.theme.colors.secondary.yellow,
    };
    return colors[props.$category as keyof typeof colors] || props.theme.colors.primary[500];
  }};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const SkillIcon = styled.div<{ $category: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => {
    const colors = {
      frontend: props.theme.colors.secondary.purple,
      backend: props.theme.colors.secondary.green,
      mobile: props.theme.colors.secondary.orange,
      database: props.theme.colors.secondary.teal,
      'ai-ml': props.theme.colors.secondary.pink,
      tools: props.theme.colors.secondary.yellow,
    };
    return colors[props.$category as keyof typeof colors] || props.theme.colors.primary[500];
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const SkillName = styled.h4`
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
`;

const SkillLevel = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  margin-top: ${props => props.theme.spacing[2]};
`;

const LevelBar = styled.div`
  flex: 1;
  height: 6px;
  background-color: ${props => props.theme.colors.neutral[200]};
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
`;

const LevelFill = styled.div<{ $level: string }>`
  height: 100%;
  background: ${props => props.theme.gradients.primary};
  width: ${props => {
    const levels = {
      beginner: '25%',
      intermediate: '50%',
      advanced: '75%',
      expert: '100%',
    };
    return levels[props.$level as keyof typeof levels] || '0%';
  }};
  transition: width ${props => props.theme.animations.duration.slow} ${props => props.theme.animations.easing.smooth};
`;

const LevelText = styled.span`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: capitalize;
`;

const ExperienceSection = styled.div``;

const ExperienceCard = styled(motion.div)`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing[6]};
  border-left: 4px solid ${props => props.theme.colors.primary[500]};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.smooth};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ExperienceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const ExperienceIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => props.theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const ExperienceInfo = styled.div`
  flex: 1;
`;

const ExperienceTitle = styled.h4`
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const ExperienceCompany = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  color: ${props => props.theme.colors.primary[600]};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const ExperienceDuration = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${props => props.theme.spacing[1]};
`;

const ExperienceDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
`;

const TechTag = styled.span`
  background-color: ${props => props.theme.colors.primary[100]};
  color: ${props => props.theme.colors.primary[700]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const About: React.FC = () => {
  const { t } = useLanguage();
  
  // Skills data with dynamic translations
  const skills: Skill[] = [
    // Frontend
    { name: 'React', level: 'advanced', category: 'frontend', icon: '⚛️' },
    { name: 'TypeScript', level: 'advanced', category: 'frontend', icon: '📘' },
    { name: 'JavaScript', level: 'expert', category: 'frontend', icon: '🟨' },
    { name: 'Flutter', level: 'intermediate', category: 'mobile', icon: '🐦' },
    { name: 'Dart', level: 'intermediate', category: 'mobile', icon: '🎯' },
    
    // Backend
    { name: 'Node.js', level: 'advanced', category: 'backend', icon: '🟢' },
    { name: 'Python', level: 'intermediate', category: 'backend', icon: '🐍' },
    { name: 'PHP', level: 'beginner', category: 'backend', icon: '🐘' },
    
    // Database
    { name: 'MongoDB', level: 'advanced', category: 'database', icon: '🍃' },
    { name: 'PostgreSQL', level: 'intermediate', category: 'database', icon: '🐘' },
    
    // AI/ML
    { name: 'Machine Learning', level: 'beginner', category: 'ai-ml', icon: '🤖' },
    { name: 'AI Integration', level: 'beginner', category: 'ai-ml', icon: '🧠' },
  ];

  // Experience data with dynamic translations
  const experiences: Experience[] = [
    {
      id: '1',
      company: t('about.experience.systra.company'),
      position: t('about.experience.systra.position'),
      description: t('about.experience.systra.description'),
      startDate: '2024-01',
      endDate: '2024-03',
      location: t('about.experience.systra.location'),
      type: 'internship',
      technologies: ['TypeScript', 'React', 'Node.js', 'PostgreSQL', 'ERP Integration'],
      achievements: [
        'Successfully integrated custom web module into existing ERP system',
        'Improved system efficiency and user experience',
        'Collaborated with cross-functional teams'
      ]
    }
  ];

  const getLevelText = (level: string) => {
    switch (level) {
      case 'beginner':
        return t('about.skills.level.beginner');
      case 'intermediate':
        return t('about.skills.level.intermediate');
      case 'advanced':
        return t('about.skills.level.advanced');
      case 'expert':
        return t('about.skills.level.expert');
      default:
        return level;
    }
  };
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      frontend: <Code size={20} />,
      backend: <Server size={20} />,
      mobile: <Smartphone size={20} />,
      database: <Database size={20} />,
      'ai-ml': <Brain size={20} />,
      tools: <Globe size={20} />,
    };
    return icons[category as keyof typeof icons] || <Code size={20} />;
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <AboutContainer>
        <ProfileCard variants={itemVariants}>
          <ProfileImage>
            <Code size={48} />
          </ProfileImage>
          <ProfileName>{t('about.profile.name')}</ProfileName>
          <ProfileTitle>{t('about.profile.title')}</ProfileTitle>
          <ProfileDescription>
            {t('about.profile.description')}
          </ProfileDescription>
          <StatsContainer>
            <StatItem>
              <StatNumber>2+</StatNumber>
              <StatLabel>{t('about.stats.experience')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>5+</StatNumber>
              <StatLabel>{t('about.stats.technologies')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>1</StatNumber>
              <StatLabel>{t('about.stats.projects')}</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>∞</StatNumber>
              <StatLabel>{t('about.stats.creativity')}</StatLabel>
            </StatItem>
          </StatsContainer>
        </ProfileCard>

        <ContentSection>
          <SkillsSection>
            <SectionTitle>{t('about.skills.title')}</SectionTitle>
            <SkillsGrid>
              {skills.map((skill, index) => (
                <SkillCard
                  key={skill.name}
                  $category={skill.category}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                >
                  <SkillHeader>
                    <SkillIcon $category={skill.category}>
                      {getCategoryIcon(skill.category)}
                    </SkillIcon>
                    <SkillName>{skill.name}</SkillName>
                  </SkillHeader>
                  <SkillLevel>
                    <LevelBar>
                      <LevelFill $level={skill.level} />
                    </LevelBar>
                    <LevelText>{getLevelText(skill.level)}</LevelText>
                  </SkillLevel>
                </SkillCard>
              ))}
            </SkillsGrid>
          </SkillsSection>

          <ExperienceSection>
            <SectionTitle>{t('about.experience.title')}</SectionTitle>
            {experiences.map((experience, index) => (
              <ExperienceCard
                key={experience.id}
                variants={itemVariants}
                whileHover={{ scale: 1.01 }}
              >
                <ExperienceHeader>
                  <ExperienceIcon>
                    <Briefcase size={20} />
                  </ExperienceIcon>
                  <ExperienceInfo>
                    <ExperienceTitle>{experience.position}</ExperienceTitle>
                    <ExperienceCompany>{experience.company}</ExperienceCompany>
                    <ExperienceDuration>
                      {experience.startDate} - {experience.endDate} • {experience.location}
                    </ExperienceDuration>
                  </ExperienceInfo>
                </ExperienceHeader>
                <ExperienceDescription>
                  {experience.description}
                </ExperienceDescription>
                <TechStack>
                  {experience.technologies.map((tech) => (
                    <TechTag key={tech}>{tech}</TechTag>
                  ))}
                </TechStack>
              </ExperienceCard>
            ))}
          </ExperienceSection>
        </ContentSection>
      </AboutContainer>
    </motion.div>
  );
};

export default About;
